/**
 * ProductScene.jsx — Aviatricks Aero Labs
 * Mini R3F scene for ProductShowcase right panel.
 * Shows a floating procedural drone + variant-specific particle environment.
 *
 * Variants:
 *   base         → purple mission path lines + waypoint nodes
 *   agri         → green flat grid particles (crop field scan)
 *   survey       → blue rotating point cloud rings
 *   surveillance → grey radar sweep ring + patrol dots
 *
 * frameloop prop passed from useCanvasPause — pauses when offscreen.
 */

import { useRef, useMemo, useEffect, useState } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { acquireWebGL, releaseWebGL, registerRenderer } from '../../utils/webglManager'

// ─── VARIANT CONFIG ────────────────────────────────────────────────────────────
const VARIANT_CONFIG = {
  base: {
    accent: new THREE.Color('#5d179e'),
    particleCount: 120,
    buildEnv: buildMissionPath,
  },
  agri: {
    accent: new THREE.Color('#2d6a4f'),
    particleCount: 200,
    buildEnv: buildAgriGrid,
  },
  survey: {
    accent: new THREE.Color('#4a6fa5'),
    particleCount: 160,
    buildEnv: buildSurveyCloud,
  },
  surveillance: {
    accent: new THREE.Color('#6b7280'),
    particleCount: 100,
    buildEnv: buildPatrolRing,
  },
}

// ─── ENV BUILDERS ─────────────────────────────────────────────────────────────
function buildMissionPath(n) {
  // Waypoint-connected path in XZ plane
  const pos = new Float32Array(n * 3)
  const waypoints = [
    [-1.8, 0, -1.2], [-0.8, 0.3, -1.8], [0.6, 0.5, -1.4],
    [1.6, 0.2, -0.4], [1.2, 0.4, 0.8], [0, 0.6, 1.4], [-1.4, 0.3, 0.9],
  ]
  for (let i = 0; i < n; i++) {
    const t = i / n
    const seg = t * (waypoints.length - 1)
    const idx = Math.min(Math.floor(seg), waypoints.length - 2)
    const frac = seg - idx
    const a = waypoints[idx]
    const b = waypoints[idx + 1]
    pos[i * 3] = a[0] + (b[0] - a[0]) * frac + (Math.random() - 0.5) * 0.12
    pos[i * 3 + 1] = a[1] + (b[1] - a[1]) * frac + (Math.random() - 0.5) * 0.08
    pos[i * 3 + 2] = a[2] + (b[2] - a[2]) * frac + (Math.random() - 0.5) * 0.12
  }
  return pos
}

function buildAgriGrid(n) {
  // Flat grid on ground — crop scan pattern
  const pos = new Float32Array(n * 3)
  const cols = Math.ceil(Math.sqrt(n))
  for (let i = 0; i < n; i++) {
    pos[i * 3] = ((i % cols) / cols - 0.5) * 5.5
    pos[i * 3 + 1] = -1.4 + (Math.random() - 0.5) * 0.12
    pos[i * 3 + 2] = (Math.floor(i / cols) / cols - 0.5) * 4
  }
  return pos
}

function buildSurveyCloud(n) {
  // Scattered 3D point cloud — terrain scan
  const pos = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) {
    const theta = Math.random() * Math.PI * 2
    const phi = Math.random() * Math.PI
    const r = 1.2 + Math.random() * 1.8
    pos[i * 3] = r * Math.sin(phi) * Math.cos(theta)
    pos[i * 3 + 1] = r * Math.cos(phi) * 0.5 - 0.6
    pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta)
  }
  return pos
}

function buildPatrolRing(n) {
  // Patrol orbit dots at two radii
  const pos = new Float32Array(n * 3)
  for (let i = 0; i < n; i++) {
    const ring = i % 2
    const r = ring === 0 ? 1.8 : 2.8
    const angle = (i / n) * Math.PI * 2 * (ring === 0 ? 1 : 1.5)
    pos[i * 3] = Math.cos(angle) * r
    pos[i * 3 + 1] = -0.8 + ring * 0.4 + (Math.random() - 0.5) * 0.1
    pos[i * 3 + 2] = Math.sin(angle) * r
  }
  return pos
}

// ─── MINI PROCEDURAL DRONE ────────────────────────────────────────────────────
function MiniDrone({ accent }) {
  const droneRef = useRef()
  const propRefs = useRef([null, null, null, null])
  const glowRef = useRef()

  const mats = useMemo(() => ({
    body: new THREE.MeshStandardMaterial({ color: '#1a1430', roughness: 0.35, metalness: 0.85 }),
    arm: new THREE.MeshStandardMaterial({ color: '#15102a', roughness: 0.5, metalness: 0.75 }),
    motor: new THREE.MeshStandardMaterial({ color: '#9e9ea6', roughness: 0.25, metalness: 0.95 }),
    prop: new THREE.MeshStandardMaterial({
      color: accent, emissive: accent, emissiveIntensity: 0.7,
      roughness: 0.15, metalness: 0.4, transparent: true, opacity: 0.88,
    }),
    sensor: new THREE.MeshStandardMaterial({
      color: accent, emissive: accent, emissiveIntensity: 1.5,
      roughness: 0.05, metalness: 0.2,
    }),
  }), [accent])

  const geos = useMemo(() => ({
    body: new THREE.BoxGeometry(0.55, 0.06, 0.55),
    arm: new THREE.BoxGeometry(0.032, 0.022, 0.78),
    motor: new THREE.CylinderGeometry(0.088, 0.08, 0.048, 16),
    prop: new THREE.TorusGeometry(0.14, 0.009, 6, 24),
    blade: new THREE.BoxGeometry(0.26, 0.004, 0.03),
    sensor: new THREE.SphereGeometry(0.028, 8, 8),
    leg: new THREE.CylinderGeometry(0.006, 0.006, 0.18, 6),
  }), [])

  const ARMS = [
    { ry: Math.PI * 0.25, mx: 0.31, mz: 0.31 },
    { ry: Math.PI * 0.75, mx: -0.31, mz: 0.31 },
    { ry: Math.PI * 1.25, mx: -0.31, mz: -0.31 },
    { ry: Math.PI * 1.75, mx: 0.31, mz: -0.31 },
  ]
  const LEGS = [
    { x: 0.18, z: 0.18 }, { x: -0.18, z: 0.18 },
    { x: -0.18, z: -0.18 }, { x: 0.18, z: -0.18 },
  ]

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    if (droneRef.current) {
      droneRef.current.position.y = 0.4 + Math.sin(t * 0.9) * 0.06
      droneRef.current.rotation.y = Math.sin(t * 0.25) * 0.12
      droneRef.current.rotation.x = Math.sin(t * 0.55) * 0.022
      droneRef.current.rotation.z = Math.cos(t * 0.45) * 0.016
    }
    propRefs.current.forEach((r, i) => {
      if (r) r.rotation.y = t * (7 + i * 0.3) * (i % 2 === 0 ? 1 : -1)
    })
    mats.sensor.emissiveIntensity = 1.2 + 0.6 * Math.sin(t * 2.5)
    if (glowRef.current) glowRef.current.intensity = 2.0 + 0.8 * Math.sin(t * 1.8)
  })

  return (
    <group ref={droneRef}>
      <mesh geometry={geos.body} material={mats.body} castShadow />
      {ARMS.map(({ ry, mx, mz }, i) => (
        <group key={i}>
          <mesh geometry={geos.arm} material={mats.arm}
            rotation={[0, ry, 0]} position={[mx * 0.5, 0, mz * 0.5]} />
          <mesh geometry={geos.motor} material={mats.motor} position={[mx, 0.025, mz]} />
          <group ref={el => propRefs.current[i] = el} position={[mx, 0.058, mz]}>
            <mesh geometry={geos.prop} material={mats.prop} rotation={[Math.PI / 2, 0, 0]} />
            <mesh geometry={geos.blade} material={mats.prop} />
            <mesh geometry={geos.blade} material={mats.prop} rotation={[0, Math.PI / 2, 0]} />
          </group>
        </group>
      ))}
      {LEGS.map(({ x, z }, i) => (
        <mesh key={i} geometry={geos.leg} material={mats.arm} position={[x, -0.11, z]} />
      ))}
      <mesh geometry={geos.sensor} material={mats.sensor} position={[0.16, 0.03, -0.19]} />
      <mesh geometry={geos.sensor} material={mats.sensor} position={[-0.16, 0.03, -0.19]} />
      <pointLight ref={glowRef} color={accent} intensity={2.0} distance={3} position={[0, 0.2, 0]} />
    </group>
  )
}

// ─── ENVIRONMENT PARTICLES ────────────────────────────────────────────────────
function EnvParticles({ variantId, accent }) {
  const posRef = useRef(null)

  const config = VARIANT_CONFIG[variantId]

  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry()
    const pos = config.buildEnv(config.particleCount)
    g.setAttribute('position', new THREE.BufferAttribute(pos.slice(), 3))
    posRef.current = pos
    return g
  }, [config]);

  const mat = useMemo(() => new THREE.PointsMaterial({
    color: accent,
    size: variantId === 'agri' ? 0.045 : 0.038,
    transparent: true,
    opacity: variantId === 'surveillance' ? 0.45 : 0.65,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    sizeAttenuation: true,
  }), [variantId, accent])

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    const pos = posRef.current
    const arr = geo.attributes.position.array

    if (variantId === 'base') {
      // Path particles pulse along route
      for (let i = 0; i < pos.length / 3; i++) {
        const j = i * 3
        arr[j + 1] = pos[j + 1] + Math.sin(t * 1.2 + i * 0.3) * 0.04
      }
    } else if (variantId === 'agri') {
      // Grid scan — ripple from center
      for (let i = 0; i < pos.length / 3; i++) {
        const j = i * 3
        const dx = pos[j], dz = pos[j + 2]
        const dist = Math.sqrt(dx * dx + dz * dz)
        arr[j + 1] = pos[j + 1] + Math.sin(t * 1.5 - dist * 2.2) * 0.06
      }
    } else if (variantId === 'survey') {
      // Point cloud slowly rotates
      for (let i = 0; i < pos.length / 3; i++) {
        const j = i * 3
        const px = pos[j], pz = pos[j + 2]
        const a = t * 0.18
        arr[j] = px * Math.cos(a) - pz * Math.sin(a)
        arr[j + 2] = px * Math.sin(a) + pz * Math.cos(a)
        arr[j + 1] = pos[j + 1] + Math.sin(t * 0.8 + i * 0.2) * 0.03
      }
    } else if (variantId === 'surveillance') {
      // Patrol ring rotates
      for (let i = 0; i < pos.length / 3; i++) {
        const j = i * 3
        const px = pos[j], pz = pos[j + 2]
        const a = t * 0.35 * (i % 2 === 0 ? 1 : -0.6)
        arr[j] = px * Math.cos(a) - pz * Math.sin(a)
        arr[j + 2] = px * Math.sin(a) + pz * Math.cos(a)
      }
    }

    geo.attributes.position.needsUpdate = true
  })

  return <points geometry={geo} material={mat} />
}

// ─── RADAR RING (surveillance only) ──────────────────────────────────────────
function RadarRing({ accent }) {
  const ringRef = useRef()
  const mat = useMemo(() => new THREE.MeshBasicMaterial({
    color: accent, transparent: true, opacity: 0.15, side: THREE.DoubleSide,
  }), [accent])
  const geo = useMemo(() => new THREE.RingGeometry(1.6, 1.62, 64), [])

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    if (ringRef.current) {
      ringRef.current.rotation.z = t * 0.4
      ringRef.current.material.opacity = 0.10 + 0.08 * Math.sin(t * 1.5)
    }
  })

  return (
    <mesh ref={ringRef} geometry={geo} material={mat}
      rotation={[-Math.PI / 2, 0, 0]} position={[0, -1.0, 0]} />
  )
}

// ─── MISSION PATH LINES (base only) ──────────────────────────────────────────
function MissionLines({ accent }) {
  const waypoints = useMemo(() => [
    [-1.8, -0.2, -1.2], [-0.8, 0.1, -1.8], [0.6, 0.3, -1.4],
    [1.6, 0.0, -0.4], [1.2, 0.2, 0.8], [0, 0.4, 1.4], [-1.4, 0.1, 0.9],
  ], [])

  const geo = useMemo(() => {
    const g = new THREE.BufferGeometry()
    const pts = waypoints.map(p => new THREE.Vector3(...p))
    const curve = new THREE.CatmullRomCurve3(pts)
    const points = curve.getPoints(80)
    g.setFromPoints(points)
    return g
  }, [waypoints])

  const mat = useMemo(() => new THREE.LineBasicMaterial({
    color: accent, transparent: true, opacity: 0.35,
  }), [accent])

  // Waypoint nodes
  const nodeGeo = useMemo(() => new THREE.SphereGeometry(0.055, 8, 8), [])
  const nodeMat = useMemo(() => new THREE.MeshBasicMaterial({
    color: accent, transparent: true, opacity: 0.7,
  }), [accent])

  return (
    <group>
      <primitive object={new THREE.Line(geo, mat)} />
      {waypoints.map((p, i) => (
        <mesh key={i} geometry={nodeGeo} material={nodeMat} position={p} />
      ))}
    </group>
  )
}

// ─── LIGHTS ───────────────────────────────────────────────────────────────────
function SceneLights({ accentHex }) {
  return (
    <>
      <ambientLight color="#e8e4f0" intensity={1.4} />
      <directionalLight color="#9e9ea6" intensity={0.8} position={[3, 5, 3]} />
      <pointLight color={accentHex} intensity={1.8} distance={8} position={[-2, 2, 1]} />
      <pointLight color={accentHex} intensity={0.8} distance={6} position={[0, -2, 2]} />
    </>
  )
}

// ─── INNER SCENE ──────────────────────────────────────────────────────────────
function InnerScene({ variantId, accentHex }) {
  const accentColor = useMemo(() => new THREE.Color(accentHex), [accentHex])
  const { camera } = useThree()

  useEffect(() => {
    camera.position.set(0, 1.8, 4.2)
    camera.lookAt(0, 0.2, 0)
  }, [camera]);

  // Gentle camera orbit
  useFrame(({ clock }) => {
    const t = clock.getElapsedTime()
    camera.position.x = Math.sin(t * 0.18) * 0.6
    camera.position.y = 1.6 + Math.sin(t * 0.12) * 0.2
    camera.lookAt(0, 0.3, 0)
  })

  return (
    <>
      <color attach="background" args={['#f5f2ee']} />
      <fog attach="fog" args={['#f5f2ee', 7, 18]} />
      <SceneLights accentHex={accentHex} />
      <MiniDrone accent={accentColor} />
      <EnvParticles variantId={variantId} accent={accentColor} />
      {variantId === 'base' && <MissionLines accent={accentColor} />}
      {variantId === 'surveillance' && <RadarRing accent={accentColor} />}
    </>
  )
}

// ─── EXPORTED CANVAS ──────────────────────────────────────────────────────────
export default function ProductScene({ variantId, accentHex, frameloop }) {
  const [ready, setReady] = useState(false)

  useEffect(() => {
    acquireWebGL()
    setReady(true)
    return () => releaseWebGL()
  }, [])

  if (!ready) return null

  return (
    <Canvas
      style={{ position: 'absolute', inset: 0, borderRadius: 'inherit' }}
      camera={{ fov: 52, near: 0.1, far: 30 }}
      dpr={[1, 1.5]}
      frameloop={frameloop}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance', failIfMajorPerformanceCaveat: false, stencil: false }}
      onCreated={({ gl }) => registerRenderer(gl)}
    >
      <InnerScene variantId={variantId} accentHex={accentHex} />
    </Canvas>
  )
}
